import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { RadioGroupField } from 'components/form';
import { useForm, useWatch } from 'react-hook-form';
import { ListEmpty, ListLoading } from 'components/shared/List';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { bpfAPI } from '../../api';
import { GroupByValue } from './exportToCsv';
import { ReportActions } from './ReportActions';
import { ReportListing } from './ReportListing';
import { collectReportFilters } from '../../lib';
import { ReportFilters } from './ReportsFilters';
import { ReportsSummary } from './ReportsSummary';
import { GROUP_BY_OPTIONS } from '../../constants';
import { useStagesReportDefaultFilters } from '../../hooks/useStagesReportDefaultFilters';
import {
  FullReportResponse,
  BPFReportAPINameSpase,
  ReportsListingFilters,
  ReportListingFilterLabels,
} from '../../types';

export const Reports = () => {
  const { initialFilters } = useStagesReportDefaultFilters();

  const [allGroupsOpen, setAllGroupsOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<ReportsListingFilters>(initialFilters);
  const [exportFilterLabels, setExportFilterLabels] = useState<ReportListingFilterLabels>({});

  const groupByForm = useForm<{ groupBy: GroupByValue }>({
    defaultValues: {
      groupBy: 'sales_representative_id',
    },
  });

  const groupBy = useWatch({
    name: 'groupBy',
    control: groupByForm.control,
  });

  const { data, loading, error, previousData } = useQuery<BPFReportAPINameSpase<FullReportResponse>>(
    bpfAPI.report.getReport,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          groupBy,
          filters: collectReportFilters(filters),
        },
      },
    }
  );

  const summary = (data ?? previousData)?.bpf?.report?.result?.entity?.summary;
  const list = (data ?? previousData)?.bpf?.report?.result?.entity?.list ?? [];

  const handleFiltersSubscribe = (filters: ReportsListingFilters, labels: ReportListingFilterLabels) => {
    setFilters(filters);
    setExportFilterLabels(labels);
  };

  return (
    <RenderWithFallback isLoading={loading && !previousData} error={error} fallBack={<ListLoading />}>
      <Stack gap="0.5rem" height={!list.length ? 'calc(100% - 1rem)' : 'auto'}>
        <ReportFilters onSubscribe={handleFiltersSubscribe} />

        <RenderWithCondition condition={!!list.length}>
          <ReportsSummary summary={summary} />
        </RenderWithCondition>

        <ReportActions
          list={list}
          loading={loading}
          filters={filters}
          groupBy={groupBy}
          setAllGroupsOpen={setAllGroupsOpen}
          exportFilterLabels={exportFilterLabels}
          groupNode={
            <RadioGroupField
              row
              name="groupBy"
              label="Group by:"
              options={GROUP_BY_OPTIONS}
              control={groupByForm.control}
            />
          }
        />

        <Box sx={{ boxShadow: ({ shadows }) => shadows[1], height: '100%' }}>
          <RenderWithCondition condition={!!list.length} fallBack={<ListEmpty />}>
            <ReportListing summary={summary} list={list} loading={loading} allGroupsOpen={allGroupsOpen} />
          </RenderWithCondition>
        </Box>
      </Stack>
    </RenderWithFallback>
  );
};
